import {Button} from '@wandb/components';
import {TargetBlank} from '@wandb/weave/common/util/links';
import numeral from 'numeral';
import React, {memo, useCallback, useMemo, useState} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Modal} from 'semantic-ui-react';

import {
  type HasSubsWithSubType,
  type Privileges,
  type SubscriptionForOrg,
} from '../../../util/accounts/pricing';
import {
  bytesInGB,
  getEstimatedArtifactReferenceCharge,
  getEstimatedStorageCharge,
  STORAGE_PRICE_PER_GB_TIER_1,
} from '../../../util/storage';
import * as urls from '../../../util/urls';
import {BillingAddressContextProvider} from './BillingAddressContext';
import {
  NewCheckoutModalContext,
  NewCheckoutModalUpdaterContext,
} from './NewCheckoutModalContext';
import {NewStorageSubscribeForm} from './NewStorageSubscribeForm';
import * as S from './StorageCheckoutModal.styles';

type NewStorageCheckoutModalProps = {
  onTransactionCompleted: () => void;
  onClose: () => void;
  totalStorageBytes?: number;
  totalTrackingBytes?: number;
  orgID: string;
  stripePaymentMethods?: Array<{}>;
  freeStorageInGB: number;
  primarySub: SubscriptionForOrg<HasSubsWithSubType> | null | undefined;
  privileges: Privileges;
  isBuyingInference?: boolean;
};

const NewStorageCheckoutModalComp = (props: NewStorageCheckoutModalProps) => {
  const {onClose} = props;
  const [showStorageSuccess, setShowStorageSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const contextValue = useMemo(() => ({submitting}), [submitting]);
  const updaterContextValue = useMemo(() => ({setSubmitting}), []);

  const onCloseWhenNotSubmitting = useCallback(() => {
    if (!submitting) {
      onClose();
    }
  }, [submitting, onClose]);

  return (
    <NewCheckoutModalContext.Provider value={contextValue}>
      <NewCheckoutModalUpdaterContext.Provider value={updaterContextValue}>
        <Modal
          open
          basic
          className="checkout-modal"
          onClose={onCloseWhenNotSubmitting}>
          {showStorageSuccess ? (
            <NewStorageSubscriptionSuccessForm {...props} onClose={onClose} />
          ) : (
            <NewStorageCheckoutModalForm
              {...props}
              onTransactionCompleted={() => {
                setShowStorageSuccess(true);
                props.onTransactionCompleted();
              }}
            />
          )}
        </Modal>
      </NewCheckoutModalUpdaterContext.Provider>
    </NewCheckoutModalContext.Provider>
  );
};
const NewStorageCheckoutModalForm = ({
  totalStorageBytes,
  totalTrackingBytes,
  orgID,
  stripePaymentMethods,
  onTransactionCompleted,
  freeStorageInGB,
  primarySub,
  privileges,
  isBuyingInference = false,
}: NewStorageCheckoutModalProps) => {
  const estimatedBill = useMemo(() => {
    const storageEstimate = getEstimatedStorageCharge(
      totalStorageBytes ?? 0,
      primarySub,
      privileges
    );
    const artifactEstimate = getEstimatedArtifactReferenceCharge(
      totalTrackingBytes ?? 0
    );
    const totalEstimate = storageEstimate + artifactEstimate;
    return totalEstimate.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  }, [totalStorageBytes, totalTrackingBytes, primarySub, privileges]);

  const storageLimitString = numeral(freeStorageInGB * bytesInGB).format('0 b');

  return (
    <S.Container>
      <S.MainContainer>
        <S.PlanOverviewSection>
          <S.HeaderSection>
            <S.Title>
              {isBuyingInference
                ? 'Activate W&B Inference'
                : 'Subscribe to storage plan'}
            </S.Title>
            {!isBuyingInference && (
              <S.Price>
                <S.CostPerGB>
                  {STORAGE_PRICE_PER_GB_TIER_1.toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  })}
                </S.CostPerGB>
                <S.Unit>/ GB</S.Unit>
              </S.Price>
            )}
          </S.HeaderSection>
          <S.Description>
            {isBuyingInference ? (
              <>
                Keep using Inference seamlessly even after your free credits run
                out. Check the{' '}
                <TargetBlank href="https://wandb.ai/site/pricing/inference">
                  cost of inference
                </TargetBlank>
                .
              </>
            ) : (
              `Your first ${storageLimitString} of storage are free, after that you only pay for what you use.`
            )}
          </S.Description>

          <S.TotalAmountSection>
            <S.DueToday>Due today</S.DueToday>
            <S.DueToday>{estimatedBill}</S.DueToday>
          </S.TotalAmountSection>

          <S.DescriptionItalic>
            Your next bill will be in 30 days.
          </S.DescriptionItalic>
        </S.PlanOverviewSection>
      </S.MainContainer>
      <S.SideContainer>
        <BillingAddressContextProvider>
          <NewStorageSubscribeForm
            organizationID={orgID}
            hasStripePaymentMethods={
              stripePaymentMethods != null && stripePaymentMethods.length > 0
            }
            onSubscriptionCompleted={onTransactionCompleted}
            isBuyingInference={isBuyingInference}
          />
        </BillingAddressContextProvider>
      </S.SideContainer>
    </S.Container>
  );
};
type NewStorageSubscriptionSuccessFormProps = {
  onClose: () => void;
  isBuyingInference?: boolean;
};

const NewStorageSubscriptionSuccessForm: React.FC<NewStorageSubscriptionSuccessFormProps> =
  React.memo(({onClose, isBuyingInference = false}) => {
    return (
      <div className="checkout pricing-panel" style={{width: '406px'}}>
        <S.UsageSubscriptionPopup>
          <S.UsageSubscriptionPopupTitle>
            {isBuyingInference
              ? 'Inference plan activated'
              : 'Additional space unlocked'}
          </S.UsageSubscriptionPopupTitle>
          <S.UsageSubscriptionPopupContent>
            {isBuyingInference
              ? 'Thanks for subscribing to an inference plan. You can now use our inference services with flexible pricing. Feel free to contact us with questions and feedback.'
              : 'Thanks for subscribing to a storage plan to unlock additional space for your ML projects. Feel free to contact us with questions and feedback.'}
          </S.UsageSubscriptionPopupContent>
          <S.ConfirmationButtons>
            <Button
              variant="secondary"
              onPress={() => {
                window.analytics?.track(
                  isBuyingInference
                    ? 'Inference plan contact us clicked'
                    : 'Storage plan contact us clicked'
                );
                // eslint-disable-next-line wandb/no-unprefixed-urls
                window.open(urls.contact());
              }}>
              Contact us
            </Button>
            <Button
              onPress={() => {
                window.analytics?.track(
                  isBuyingInference
                    ? 'Inference confirmation OK clicked'
                    : 'Storage confirmation OK clicked'
                );
                onClose();
              }}>
              OK
            </Button>
          </S.ConfirmationButtons>
        </S.UsageSubscriptionPopup>
      </div>
    );
  });

export const NewStorageCheckoutModal = memo(NewStorageCheckoutModalComp);
